import { useState, useRef, useCallback, useEffect } from 'react'
import { Plus, Upload, Search, Download, Loader2, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { ColumnSelectorPopover } from './ColumnSelectorPopover'
import { LineItemFilterButton } from './LineItemFilterButton'
import type { DagColumnMeta, DropdownOptionRead } from '@/types/dag'
import type { FilterGroup } from '@/types'

interface ProductsTableToolbarProps {
  onSearch?: (query: string) => void
  onAddItem?: () => void
  onImport?: () => void
  onExport?: () => void
  isExporting?: boolean
  columns?: DagColumnMeta[]
  groups?: string[]
  isReadOnly?: boolean
  onOpenColumnsConfig?: () => void
  filterableColumns?: DagColumnMeta[]
  dropdownMap?: Record<string, DropdownOptionRead[]>
  filterGroup?: FilterGroup | null
  onApplyFilter?: (filter: FilterGroup | null) => void
  isFilterFetching?: boolean
  activeViewId?: string | null
  onSelectView?: (viewId: string | null) => void
  getCurrentColumnIds?: () => string[]
}

export function ProductsTableToolbar({
  onSearch,
  onAddItem,
  onImport,
  onExport,
  isExporting,
  columns,
  groups,
  isReadOnly,
  onOpenColumnsConfig,
  filterableColumns,
  dropdownMap,
  filterGroup,
  onApplyFilter,
  isFilterFetching,
  activeViewId,
  onSelectView,
  getCurrentColumnIds,
}: ProductsTableToolbarProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedSearch = useCallback(
    (value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        onSearch?.(value)
      }, 300)
    },
    [onSearch]
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)
    debouncedSearch(value)
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          className="gap-2 border-primary text-primary hover:bg-primary/10 cursor-pointer"
          onClick={onAddItem}
          disabled={isReadOnly}
        >
          <Plus className="h-4 w-4" />
          Add Item
        </Button>
        <Button
          variant="ghost"
          className="gap-2 cursor-pointer text-primary"
          onClick={onImport}
          disabled={isReadOnly}
        >
          <Upload className="h-4 w-4" />
          Import
        </Button>
      </div>
      <div className="flex flex-1 items-center justify-end gap-3 min-w-0">
        <div className="relative w-full max-w-[352px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-9 bg-muted/50"
          />
        </div>
        {filterableColumns && filterableColumns.length > 0 && (
          <LineItemFilterButton
            columns={filterableColumns}
            dropdownMap={dropdownMap ?? {}}
            filter={filterGroup ?? null}
            onApplyFilter={onApplyFilter ?? (() => {})}
            isApplying={isFilterFetching}
          />
        )}
        <Separator orientation="vertical" className="h-10" />
        <Button
          variant="outline"
          className="gap-2 cursor-pointer"
          onClick={onExport}
          disabled={isExporting}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export
        </Button>
        {columns && groups && (
          <ColumnSelectorPopover
            columns={columns}
            groups={groups}
            activeViewId={activeViewId}
            onSelectView={onSelectView}
            getCurrentColumnIds={getCurrentColumnIds}
          />
        )}
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 cursor-pointer"
          onClick={onOpenColumnsConfig}
        >
          <Settings className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
